<script setup lang="ts">
import {
  IElement,
  ELEMENTS_LOOKUP,
  SignatureElementOptions,
  ProductListElementOptions,
} from '@gohighlevel/ghl-proposals-common'
import { PropType, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import CProductList from '../elements/ProductList/CProductList.vue'
import CTable from '../elements/CTable.vue'
import CText from '../elements/CText.vue'
import CVideo from '../elements/CVideo.vue'
import CImage from '../elements/CImage.vue'
import CSignature from '../elements/CSignature.vue'
import FloatingElementRenderer from './FloatingElementRenderer.vue'
import { useDocument } from '@/composable'
import { useMarkerForEditor } from '@/composable/useMarkerForEditor'
const PrintAdjustBlocks = [
  ELEMENTS_LOOKUP.TEXT,
  ELEMENTS_LOOKUP.IMAGE,
  ELEMENTS_LOOKUP.TABLE,
]

const props = defineProps({
  element: {
    required: true,
    type: Object as PropType<IElement<any>>,
  },
  pageId: {
    required: true,
    type: String,
  },
  page: {
    required: true,
    type: Object as PropType<IElement>,
  },
  pageRef: {
    required: false,
    type: Object as () => HTMLElement,
  },
})
const parentRef = ref<HTMLElement>()
const localPageRef = ref<HTMLElement | undefined>(props.pageRef)
const { documentVersion } = useDocument()
const { isPrintAdjustToConsider } = useMarkerForEditor()

watch(
  () => props.pageRef,
  newVal => {
    localPageRef.value = newVal
  },
  { immediate: true }
)

const getDraggableElement = (children: IElement[]) => {
  if (!children) return []
  return children.filter(element => element && element?.component?.isDraggable)
}
const parentDimensions = ref<DOMRect>()

const resizeObserver = ref<ResizeObserver | null>(null)

const applyPrintAdjustment = computed(() => {
  return (
    !PrintAdjustBlocks.includes(props.element.type) ||
    (documentVersion.value ?? 1) === 1 ||
    isPrintAdjustToConsider
  )
})

onMounted(() => {
  if (parentRef.value) {
    resizeObserver.value = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height
        if (parentDimensions.value?.height !== newHeight) {
          parentDimensions.value = entry.contentRect
        }
      }
    })

    resizeObserver.value.observe(parentRef.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
})
</script>

<template>
  <div class="relative clearfix" style="width: 100%">
    <section
      style="transform: translateZ(0); width: inherit"
      class="flex relative print:block;"
      v-if="!element.component.isDraggable"
      ref="parentRef"
    >
      <FloatingElementRenderer
        :key="parentDimensions?.height + 'parentDimensions'"
        :elements="getDraggableElement(element?.children)"
        :pageId="pageId"
        :page="page"
        :pageRef="pageRef"
        :parentRef="parentRef"
        :parentDimensions="parentDimensions"
        :isParentPage="false"
        :applyPrintAdjustment="applyPrintAdjustment"
      />

      <CText
        :element="element"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.TEXT"
      />
      <CImage
        :element="element"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.IMAGE"
      />
      <CVideo
        :element="element"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.VIDEO"
      />
      <CTable
        :element="element"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.TABLE"
      />
      <CSignature
        :element="(element as IElement<SignatureElementOptions>)"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.SIGNATURE"
        :applyPrintAdjustment="applyPrintAdjustment"
      />
      <CProductList
        :element="(element as IElement<ProductListElementOptions>)"
        :pageId="pageId"
        v-if="element.type === ELEMENTS_LOOKUP.PRODUCT_LIST"
      />
    </section>
  </div>
</template>
